import { normalizePhone, cleanPhoneInput, formatPhone, INVALID_PHONE_MSG } from "@/lib/phone";
import React, { useEffect, useMemo, useState } from "react";
import api, { API, getAuthToken, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ItemPicker from "@/components/ItemPicker";
import { useAuth } from "@/context/AuthContext";
import { BillingModeBar, CostPreviewBox, calcCommission, orderPickerItems } from "@/components/SheetBilling";
import CommissionLines, { commissionLines, staffTotal, welderTotal, welderErrors } from "@/components/CommissionLines";
import {
  Plus, Trash, Calculator, User, Phone as PhoneIcon, ImageSquare, X, MapPin
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { istToday } from "@/lib/istDate";
import GateWorkItems, { blankWorkItem } from "@/components/GateWorkItems";

const blankItem = () => ({
  item_id: "",
  item_name: "",
  thickness: "",
  rate_per_sqft: 250,
  width_in: 48,
  height_in: 96,
  quantity: 1,
  sizing_mode: "standard",  // "standard" = margin on, "exact" = as-is
  margin_inches: 3,         // 3" for MS (default), 6" for SS (auto-detected from name)
  unit: "sqft",             // "sqft", "piece", "set", "meter", "kg"
  discount_amount: 0,       // per-row discount (₹) — used when discount_mode = "amount"
  discount_percent: 0,      // per-row discount (%) — used when discount_mode = "percent"
  discount_mode: "amount",  // UI toggle: "amount" | "percent"
  size_unit: "inch",        // input display unit for W/H: "inch" | "mm"
});

// Line discount: "amount" (₹ flat) | "percent" | "per_sqft" (sqft lines only). Never above gross.
const lineDiscount = (it, gross, sqft) => {
  const mode = it.discount_mode || "amount";
  const d = mode === "percent" ? (gross * (Number(it.discount_percent) || 0)) / 100
    : mode === "per_sqft" ? (sqft > 0 ? sqft * (Number(it.discount_per_sqft) || 0) : 0)
    : Number(it.discount_amount) || 0;
  return Math.min(Math.max(d, 0), gross);
};
const DISC_NEXT = { amount: "percent", percent: "per_sqft", per_sqft: "amount" };
const DISC_LABEL = { amount: "₹", percent: "%", per_sqft: "₹/ft" };
const DISC_FIELD = { amount: "discount_amount", percent: "discount_percent", per_sqft: "discount_per_sqft" };
const lineSig = (it) => `${it.item_id}|${Number(it.rate_per_sqft) || 0}|${it.discount_mode}|${Number(it[DISC_FIELD[it.discount_mode || "amount"]]) || 0}`;
const LIST_CACHE_KEY = "sc_order_lists_v1";
let _listCache = (() => { try { return JSON.parse(localStorage.getItem(LIST_CACHE_KEY)) || null; } catch (_e) { return null; } })();
const saveListCache = (patch) => {
  _listCache = { modes: [], welders: [], items: [], fabricators: [], ..._listCache, ...patch };
  try { localStorage.setItem(LIST_CACHE_KEY, JSON.stringify(_listCache)); } catch (_e) { /* quota */ }
};
const settle = (p, fallback) => p.then((r) => r.data ?? fallback).catch(() => fallback);

// mm ↔ inch conversion. 1 inch = 25.4 mm (exact).
const MM_PER_INCH = 25.4;
// Convert the raw W/H input on a row → inches for calculation / API payload.
// Rounds to 4 decimals to avoid float-precision drift (e.g. 1219.2 / 25.4 →
// 48.00000000000001) which used to bump the margin-snap up an extra step.
const toInches = (val, sizeUnit) => {
  const n = Number(val) || 0;
  if (sizeUnit === "mm") return Math.round((n / MM_PER_INCH) * 10000) / 10000;
  if (sizeUnit === "ft") return n * 12;
  return n;
};

// Detect appropriate margin (inches) for a material name.
// SS (Stainless Steel) → 6", everything else (MS default) → 3".
const marginForItemName = (name = "") => {
  return /\bSS\b|stainless/i.test(String(name)) ? 6 : 3;
};

// Parse a thickness like "2mm" out of an item name.
const parseThickness = (name = "") => {
  const s = String(name);
  let m = s.match(/(\d+(?:\.\d+)?)\s*mm/i);
  if (m) return `${m[1]}mm`;
  m = s.match(/(\d+(?:\.\d+)?)\s*(?:g|ga|gauge)\b/i);
  if (m) return `${m[1]}g`;
  return null;
};

// Snap up to next multiple of `step`. Tiny epsilon prevents float noise
// (e.g. 48.0000000001) from ticking to the next multiple.
const snapMargin = (v, step = 3) => {
  const n = Number(v) || 0;
  const s = Number(step) || 3;
  if (n <= 0) return 0;
  return Math.ceil(n / s - 1e-6) * s;
};

export default function NewOrderForm({ onCreated, mode = "order", initialOrder = null, onSaved = null, onCancelEdit = null, editKind = "order", onBack = null }) {
  const isEstimate = mode === "estimate";
  const isEdit = !!initialOrder;
  const isEstimateEdit = isEdit && editKind === "estimate";
  const [welders, setWelders] = useState([]);
  const [welderId, setWelderId] = useState(""); // linked welder id if picked
  const [materialItems, setMaterialItems] = useState([]); // sheet items from /items
  const [customer, setCustomer] = useState("");
  const [mobile, setMobile] = useState("");
  const [address, setAddress] = useState("");
  const [projectType, setProjectType] = useState("sheet_cutting");
  const [workType, setWorkType] = useState("gate");
  const plateFlow = projectType === "gate_railing" && (!isEdit || !!initialOrder?.plate_flow);
  const gateV2 = projectType === "gate_railing" && !isEstimate && !isEstimateEdit && (!isEdit || !!initialOrder?.gate_v2);
  const [fabRates, setFabRates] = useState({ columns: [], rows: [] });
  const ptRef = React.useRef(projectType);
  ptRef.current = projectType;
  const [attachSpec, setAttachSpec] = useState(initialOrder?.attach_spec !== false);
  useEffect(() => {
    if (!gateV2) return;
    api.get("/fab-rates").then(({ data }) => setFabRates(data)).catch(() => toast.error("Fabrication rates load nahi hue"));
    if (!isEdit) {
      setItems((prev) => {
        const kept = prev.filter((it) => it.work_item || it.item_id);
        return kept.some((it) => it.work_item) ? kept : [blankWorkItem(), ...kept];
      });
      api.get("/orders/next-number?project_type=gate_railing").then(({ data }) => {
        if (data?.next_order_number && ptRef.current === "gate_railing") { setAutoOrderNumber(data.next_order_number); setCustomOrderNumber(data.next_order_number); }
      }).catch(() => {});
    }
  }, [gateV2]); // eslint-disable-line react-hooks/exhaustive-deps
  const [partyAdvance, setPartyAdvance] = useState(0);
  useEffect(() => {
    const m = normalizePhone(mobile);
    if (!m || isEdit) { setPartyAdvance(0); return undefined; }
    let off = false;
    api.get(`/parties/${m}/balance`).then(({ data }) => { if (!off) setPartyAdvance(Number(data?.advance_credit || 0)); }).catch(() => {});
    return () => { off = true; };
  }, [mobile, isEdit]);

  useEffect(() => {
    if (isEdit || projectType !== "sheet_cutting") return;
    setItems((prev) => {
      if (!prev.some((it) => it.work_item)) return prev;
      const kept = prev.filter((it) => !it.work_item);
      return kept.length ? kept : [blankItem()];
    });
    api.get("/orders/next-number").then(({ data }) => {
      if (data?.next_order_number && ptRef.current === "sheet_cutting") { setAutoOrderNumber(data.next_order_number); setCustomOrderNumber(data.next_order_number); }
    }).catch(() => {});
  }, [projectType]); // eslint-disable-line react-hooks/exhaustive-deps
  const commMaterials = useMemo(() => [...materialItems, ...fabRates.rows.map((r) => ({
    id: `fab:${r.id}`, name: `${r.type_label} ${r.sub_label}`, category: "fabrication",
    company_price: r.company_rate || 0, mrp: 1e9, welder_rate_sqft: r.welder_rate || 0,
  }))], [materialItems, fabRates]);
  const [billingMode, setBillingMode] = useState("per_sqft");
  const [wKg, setWKg] = useState("");
  const [wRate, setWRate] = useState("");
  const { user: authUser } = useAuth();
  const isAdmin = authUser?.role === "admin";
  const canComm = isAdmin || authUser?.role === "secondary_admin";
  const [machineCost, setMachineCost] = useState(18);
  const [customOrderNumber, setCustomOrderNumber] = useState("");
  // Track the auto-fetched preview so we only send `custom_order_number` when
  // the user has ACTUALLY edited the field. Otherwise the backend counter
  // wouldn't increment and every new order would collide on the same number.
  const [autoOrderNumber, setAutoOrderNumber] = useState("");
  const [customDate, setCustomDate] = useState(""); // yyyy-mm-dd
  const [saveAsWelder, setSaveAsWelder] = useState(false);
  const [shopName, setShopName] = useState("");
  const [notes, setNotes] = useState("");
  const [items, setItems] = useState([blankItem()]);
  const [advanceAmount, setAdvanceAmount] = useState("");
  const [advanceMode, setAdvanceMode] = useState("cash");
  const [modes, setModes] = useState([
    { key: "cash", label: "Cash" },
    { key: "online_sbi", label: "Online — SBI A/c" },
    { key: "online_hdfc", label: "Online — HDFC A/c" },
  ]);
  const [refImages, setRefImages] = useState([]);
  // In edit mode we also carry the ALREADY-uploaded reference images so the
  // form visually matches the "new order" layout. Each entry: {id, filename}.
  const [existingRefs, setExistingRefs] = useState([]);
  const [designDescription, setDesignDescription] = useState("");
  const [fabricators, setFabricators] = useState([]);
  const [fabricatorId, setFabricatorId] = useState("");
  const [deadlineDate, setDeadlineDate] = useState("");
  const [isEmergency, setIsEmergency] = useState(false);
  const [busy, setBusy] = useState(false);

  // ─────── Welder commission override (iter42) ───────
  // When the owner wants to pay a specific welder commission out of this
  // order's margin (instead of leaving it as staff commission based on
  // MRP/CP), these fields are set via the "Pay commission to welder"
  // section below. Mutually exclusive with staff commission.
  const [commEnabled, setCommEnabled] = useState(false);
  const [commWelderId, setCommWelderId] = useState("");
  const [commWelderName, setCommWelderName] = useState("");
  const [commWelderMobile, setCommWelderMobile] = useState("");
  const [commAmount, setCommAmount] = useState("");
  const [commSearch, setCommSearch] = useState("");
  const [commNote, setCommNote] = useState("");
  const [staffComm, setStaffComm] = useState(false);
  const [staffRates, setStaffRates] = useState({});    // idx → ₹/sqft (admin override)
  const [welderRates, setWelderRates] = useState({});  // idx → ₹/sqft typed
  const [listsLoaded, setListsLoaded] = useState(!!_listCache?.items?.length);
  const [itemsError, setItemsError] = useState("");
  const loadItems = async () => {
    setItemsError("");
    try {
      const { data } = await api.get("/items", { timeout: 20000 });
      setMaterialItems(data || []);
      saveListCache({ items: data || [] });
      setListsLoaded(true);
    } catch (e) {
      if (!_listCache?.items?.length) setItemsError(e?.response?.data?.detail || "Items load nahi hue");
      else setListsLoaded(true);
    }
  };
  const commV2 = !isEdit || !!initialOrder?.comm_v2;
  const [commManual, setCommManual] = useState(false);
  const welderPaid = !!(isEdit && initialOrder?.welder_commission?.paid);

  // ─────── Phase 3A: Sales GST customer fields (optional) ───────
  const [bizGst, setBizGst] = useState(null);   // fetched once
  const [showGstPanel, setShowGstPanel] = useState(false);
  const [custGstRegistered, setCustGstRegistered] = useState(false);
  const [custGstin, setCustGstin] = useState("");
  const [custState, setCustState] = useState("");
  const [custStateCode, setCustStateCode] = useState("");
  const [advGstHint, setAdvGstHint] = useState("unspecified");
  const gstEnabled = !!(bizGst && bizGst.gst_registered
                        && bizGst.sales_gst_go_live_at);

  useEffect(() => {
    (async () => {
      try {
        if (_listCache) {
          const c = _listCache;
          if (c.modes?.length) { setModes(c.modes); setAdvanceMode(c.modes[0].key); }
          setWelders(c.welders || []); setMaterialItems(c.items || []); setFabricators(c.fabricators || []);
        }
        loadItems();
        const [m, w, nn, fabs] = await Promise.all([
          settle(api.get("/settings/payment-modes"), null),
          settle(api.get("/welders"), null),
          isEdit ? Promise.resolve(null) : settle(api.get(`/orders/next-number?project_type=${projectType}`), null),
          settle(api.get("/fabricators"), null),
        ]);
        if (m) saveListCache({ modes: m });
        if (w) saveListCache({ welders: w });
        if (fabs) saveListCache({ fabricators: fabs });
        if (m?.length) { setModes(m); setAdvanceMode(m[0].key); }
        if (w) setWelders(w);
        if (isAdmin) api.get("/settings/costing").then(({ data }) => setMachineCost(data.machine_cost_per_sqft)).catch(() => {});
        if (fabs) setFabricators(fabs);
        const expectGt = ptRef.current === "gate_railing";
        if (nn?.next_order_number && nn.next_order_number.startsWith("GT-") === expectGt) {
          setCustomOrderNumber(nn.next_order_number);
          setAutoOrderNumber(nn.next_order_number);
        }
        try {
          const { data: bg } = await api.get("/settings/business-gst");
          setBizGst(bg || null);
          if (bg?.state && !custState) setCustState(bg.state);
          if (bg?.state_code && !custStateCode) setCustStateCode(bg.state_code);
          if (bg?.gst_registered && bg?.sales_gst_go_live_at) setShowGstPanel(true);
        } catch (_e) { /* GST not configured yet */ }
      } catch (_e) { /* keep defaults */ }
    })();
    if (!isEdit) setCustomDate(istToday());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit]);

  // In edit mode, hydrate form state from the incoming order snapshot.
  // Runs once when initialOrder id changes.
  useEffect(() => {
    if (!isEdit || !initialOrder) return;
    const o = initialOrder;
    setCustomer(o.customer_name || "");
    setMobile(o.mobile || "");
    setAddress(o.address || "");
    setProjectType(o.project_type || "sheet_cutting");
    setWorkType(o.work_type || "gate");
    setBillingMode(o.billing_mode || "per_sqft");
    if (o.wholesale) { setWKg(o.wholesale.weighed_kg ?? ""); setWRate(o.wholesale.rate_per_kg ?? ""); }
    setCustomOrderNumber(o.order_number || "");
    setAutoOrderNumber(o.order_number || "");
    setCustomDate(o.created_at ? String(o.created_at).slice(0, 10) : "");
    setWelderId(o.welder_id || o.welder?.id || "");
    setDesignDescription(o.design_description || "");
    setNotes(o.notes || "");
    setDeadlineDate(o.deadline_date || "");
    const wc = o.welder_commission;
    setStaffComm(!wc && (o.allow_staff_commission ?? Number(o.total_commission) > 0));
    if (wc) {
      setCommEnabled(true); setCommManual(true);
      setCommWelderId(wc.welder_id || ""); setCommWelderName(wc.welder_name || "");
      setCommWelderMobile(wc.welder_mobile || ""); setCommSearch(wc.welder_name || "");
      setCommAmount(String(wc.amount ?? "")); setCommNote(wc.note || "");
    }
    setFabricatorId(o.assigned_fabricator_id || o.fabricator_id || "");
    setExistingRefs(Array.isArray(o.reference_images) ? o.reference_images.map((r) => ({
      id: r.id, filename: r.original_filename || r.filename || "image",
    })) : []);
    const src = Array.isArray(o.items) && o.items.length ? o.items : [];
    const mapped = src.length ? src.map((it) => {
      const nm = it.item_name || "";
      const isSS = /\bSS\b|stainless/i.test(String(nm));
      return {
        item_id: it.item_id || "",
        item_name: nm,
        thickness: it.thickness || "",
        rate_per_sqft: Number(it.rate_per_sqft) || 0,
        // Prefer RAW dims (before margin bump) — same convention as the
        // legacy inline Edit Sheets flow so users don't compound margins.
        width_in: it.width_in_raw ?? it.width_in ?? 0,
        height_in: it.height_in_raw ?? it.height_in ?? 0,
        quantity: it.quantity ?? 1,
        sizing_mode: it.sizing_mode || "standard",
        sizing_mode_w: it.sizing_mode_w || it.sizing_mode || "standard",
        sizing_mode_h: it.sizing_mode_h || it.sizing_mode || "standard",
        margin_inches: it.margin_inches ?? (isSS ? 6 : 3),
        margin_w_inches: it.margin_w_inches ?? it.margin_inches ?? (isSS ? 6 : 3),
        margin_h_inches: it.margin_h_inches ?? it.margin_inches ?? (isSS ? 6 : 3),
        unit: (it.unit || "sqft").toLowerCase(),
        discount_amount: Number(it.discount_amount) || 0,
        discount_percent: Number(it.discount_percent) || 0,
        discount_per_sqft: Number(it.discount_per_sqft) || 0,
        discount_mode: (Number(it.discount_percent) || 0) > 0 ? "percent" : (Number(it.discount_per_sqft) || 0) > 0 ? "per_sqft" : "amount",
        size_unit: "inch",
        ...(it.work_item ? { fab_rate_id: it.fab_rate_id || null, work_item: { ...it.work_item, size_unit: "inch" } } : {}),
      };
    }) : [blankItem()];
    mapped.forEach((m) => { m.orig_sig = lineSig(m); });
    setItems(mapped);
    if (o.comm_v2) {
      const sr = {};
      (o.staff_commission?.lines || []).forEach((l) => { sr[l.idx] = l.rate; });
      setStaffRates(sr);
      const wr = {};
      (wc?.lines || []).forEach((l) => { wr[l.idx] = l.rate; });
      setWelderRates(wr);
    }
  }, [isEdit, initialOrder]);

  // Auto-detect existing welder as mobile is typed
  useEffect(() => {
    const m = mobile.trim();
    if (!m || m.length < 4) { setWelderId(""); return; }
    const match = welders.find((w) => w.mobile === m);
    if (match) {
      setWelderId(match.id);
      if (!customer.trim()) setCustomer(match.name);
      if (!address.trim() && match.address) setAddress(match.address);
      setSaveAsWelder(false);
    } else {
      setWelderId("");
    }
  }, [mobile, welders, customer, address]);

  // Auto-detect existing welder as customer NAME is typed (exact match binds it)
  useEffect(() => {
    if (welderId) return; // already linked via mobile
    const n = customer.trim().toLowerCase();
    if (!n || n.length < 3) return;
    const match = welders.find((w) => (w.name || "").toLowerCase() === n);
    if (match) {
      setWelderId(match.id);
      if (!mobile.trim()) setMobile(match.mobile || "");
      if (!address.trim() && match.address) setAddress(match.address);
      setSaveAsWelder(false);
    }
  }, [customer, welders, welderId, mobile, address]);

  // Name suggestions (contains match) — shown below the name input while typing
  const [showNameSuggest, setShowNameSuggest] = useState(false);
  const nameSuggestions = useMemo(() => {
    const n = customer.trim().toLowerCase();
    if (!n || welderId) return [];
    return welders
      .filter((w) => (w.name || "").toLowerCase().includes(n))
      .slice(0, 6);
  }, [customer, welders, welderId]);

  // ─── Existing-customer autofill by mobile ───────────────────────────
  // As the operator types a 10-digit mobile, we hit /customers/lookup and,
  // if a match is found (and this is a fresh new order — not an edit and
  // no welder yet linked), prefill customer name + address (only when the
  // fields are still blank, so we never overwrite what the user typed).
  // Shows a small "EXISTING CUSTOMER · Nth order" chip so the operator
  // knows this is a repeat client.
  const [existingCustomer, setExistingCustomer] = useState(null);
  useEffect(() => {
    // Only auto-lookup for one-time (walk-in) customers; welder path
    // already handles its own binding.
    if (isEdit || welderId) { setExistingCustomer(null); return; }
    const digits = mobile.replace(/\D/g, "");
    if (!normalizePhone(mobile)) { setExistingCustomer(null); return; }
    const ctrl = new AbortController();
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/customers/lookup?mobile=${encodeURIComponent(normalizePhone(mobile))}`, { signal: ctrl.signal });
        if (data?.exists) {
          setExistingCustomer(data);
          setCustomer((prev) => prev.trim() ? prev : (data.customer_name || ""));
          setAddress((prev) => prev.trim() ? prev : (data.address || ""));
        } else {
          setExistingCustomer(null);
        }
      } catch (_e) { /* ignore aborts / errors */ }
    }, 350);
    return () => { clearTimeout(t); ctrl.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mobile, welderId, isEdit]);

  const pickWelder = (w) => {
    setWelderId(w.id);
    setCustomer(w.name || "");
    setMobile(w.mobile || "");
    setAddress(w.address || "");
    setSaveAsWelder(false);
    setShowNameSuggest(false);
  };

  const totals = useMemo(() => {
    let sqft = 0, amount = 0, grossSum = 0;
    const lines = [];
    items.forEach((it, idx) => {
      const unit = (it.unit || "sqft").toLowerCase();
      const rate = Number(it.rate_per_sqft) || 0;
      const qty = Number(it.quantity) || 0;
      let gross = 0;
      if (unit === "sqft") {
        // Per-axis margins: use `margin_w_inches` / `margin_h_inches` if
        // present (that's what the UI writes to), fall back to the legacy
        // single `margin_inches` field for older rows. Same story for the
        // sizing mode toggle — it's now per-axis.
        // NOTE: Margins are always in INCHES (business rule) regardless of
        // the row's size_unit — customer / business measures margin in inch.
        const mW = Number(it.margin_w_inches ?? it.margin_inches ?? 3) || 0;
        const mH = Number(it.margin_h_inches ?? it.margin_inches ?? 3) || 0;
        const modeW = (it.sizing_mode_w ?? it.sizing_mode) || "standard";
        const modeH = (it.sizing_mode_h ?? it.sizing_mode) || "standard";
        // Convert W/H raw input → inches based on the row's chosen unit.
        const su = it.size_unit || "inch";
        const wIn = toInches(it.width_in, su);
        const hIn = toInches(it.height_in, su);
        const w = modeW === "standard" ? snapMargin(wIn, mW) : wIn;
        const h = modeH === "standard" ? snapMargin(hIn, mH) : hIn;
        const s = (w * h * qty) / 144;
        sqft += s;
        gross = s * rate;
        lines.push({ idx, item_id: it.item_id || (it.fab_rate_id ? `fab:${it.fab_rate_id}` : it.item_id), item_name: it.item_name, sqft: s, rate, net: gross - lineDiscount(it, gross, s) });
      } else {
        gross = qty * rate;
        lines.push({ idx, item_id: it.item_id, item_name: it.item_name, sqft: 0, rate, mult: qty, net: gross - lineDiscount(it, gross, 0) });
      }
      grossSum += gross;
      amount += Math.max(gross - lineDiscount(it, gross, unit === "sqft" ? lines[lines.length - 1].sqft : 0), 0);
    });
    if (billingMode === "wholesale") amount = (Number(wKg) || 0) * (Number(wRate) || 0);
    return { sqft: round3(sqft), amount: round2(amount), gross: round2(grossSum), lines };
  }, [items, billingMode, wKg, wRate]);
  const autoComm = useMemo(() => calcCommission(totals.lines, commMaterials), [totals, commMaterials]);
  useEffect(() => {
    if (commEnabled && !commManual && !welderPaid) setCommAmount(autoComm > 0 ? String(autoComm) : "");
  }, [commEnabled, commManual, autoComm, welderPaid]);
  const changeBillingMode = (mode) => {
    setBillingMode(mode);
    setItems((prev) => prev.map((it) => {
      const m = materialItems.find((x) => x.id === it.item_id);
      if (!m || String(m.category || "").toLowerCase() === "hardware" || m.item_type === "SERVICE") return it;
      const jw = Number(m.job_work_rate_sqft) > 0 ? Number(m.job_work_rate_sqft) : "";
      return { ...it, rate_per_sqft: mode === "job_work" ? jw : (Number(m.sale_price) || it.rate_per_sqft) };
    }));
  };
  const cLines = useMemo(() => commissionLines(totals, commMaterials), [totals, commMaterials]);
  const staffAmt = Math.round(staffTotal(cLines, staffRates) * 100) / 100;
  const staffPossible = !commV2 || cLines.some((l) => l.auto > 0);
  useEffect(() => {
    if (listsLoaded && commV2 && staffComm && !staffPossible) setStaffComm(false);
  }, [listsLoaded, commV2, staffComm, staffPossible]);
  const welderAmt = commV2 ? Math.round(welderTotal(cLines, welderRates) * 100) / 100 : Number(commAmount) || 0;
  const commRatePayload = (idx) => (!commV2 ? {} : {
    staff_comm_rate_sqft: isAdmin && staffComm && !commEnabled && staffRates[idx] != null ? Number(staffRates[idx]) : null,
    welder_comm_rate_sqft: commEnabled && welderRates[idx] != null ? Number(welderRates[idx]) || 0 : null,
  });
  // Line rate warnings: below company rate (per sqft mode) and below item Job Work rate.
  const rateWarning = (it, eff) => {
    const m = materialItems.find((x) => x.id === it.item_id);
    if (!m || !(Number(it.quantity) > 0)) return null;
    if (billingMode === "job_work") {
      const jw = Number(m.job_work_rate_sqft || 0);
      if ((it.unit || "sqft") === "sqft" && !(Number(it.rate_per_sqft) > 0)) return { level: "red", text: "Job Work rate type karein" };
      return jw > 0 && Number(it.rate_per_sqft) < jw ? { level: "yellow", text: `Job Work rate ₹${jw} se kam` } : null;
    }
    const cp = Number(m.company_price || 0);
    if (billingMode !== "per_sqft" || !(cp > 0) || eff >= cp - 0.005 || (isEdit && it.orig_sig === lineSig(it))) return null;
    return { level: "yellow", text: `Company rate se kam (₹${cp}) — commission 0` };
  };

  const advNum = Number(advanceAmount) || 0;
  const advanceAdjust = isEdit ? 0 : round2(Math.min(partyAdvance, Math.max(totals.amount - advNum, 0)));
  const balance = round2(totals.amount - advNum - advanceAdjust);

  const updateItem = (idx, field, val) => {
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, [field]: val } : it)));
  };

  // Toggle the size unit for a single row AND convert the currently typed
  // W/H values so nothing "disappears" — clicking the INCH pill on a row
  // showing 48 in flips to MM and the input becomes 1219.2. Clicking again
  // flips back to 48 in. Margins stay in inches (business rule).
  const toggleRowUnit = (idx) => {
    setItems((prev) => prev.map((it, i) => {
      if (i !== idx) return it;
      const cur = it.size_unit || "inch";
      const next = cur === "inch" ? "mm" : "inch";
      const factor = next === "mm" ? MM_PER_INCH : (1 / MM_PER_INCH);
      const conv = (v) => {
        const n = Number(v);
        if (!Number.isFinite(n) || n === 0) return v;
        return Math.round(n * factor * 100) / 100;
      };
      return {
        ...it,
        size_unit: next,
        width_in: conv(it.width_in),
        height_in: conv(it.height_in),
      };
    }));
  };

  // When user picks an item from the Items master, auto-fill thickness + rate.
  const pickItem = (idx, itemId) => {
    if (!itemId) {
      updateItem(idx, "item_id", "");
      updateItem(idx, "item_name", "");
      return;
    }
    const m = materialItems.find((x) => x.id === itemId);
    if (!m) return;
    const parsedThk = parseThickness(m.name) || null;
    const isHwPick = String(m.category || "").toLowerCase() === "hardware";
    const jobWork = billingMode === "job_work" && !isHwPick;
    const rate = jobWork ? (Number(m.job_work_rate_sqft) > 0 ? Number(m.job_work_rate_sqft) : "") : (Number(m.sale_price) || 0);
    const cat = String(m.category || "sheet").toLowerCase();
    const isHardware = cat === "hardware";
    // Hardware / gate-railing / any non-sheet ready-made item: cutting size
    // (W × H, margins) is not applicable — force a non-sqft unit so the size
    // cell collapses to "—" in the UI.
    const masterUnit = (m.unit || "sqft").toLowerCase();
    const resolvedUnit = isHardware
      ? (masterUnit && masterUnit !== "sqft" ? masterUnit : "piece")
      : masterUnit;
    setItems((prev) => prev.map((it, i) => {
      if (i !== idx) return it;
      const next = {
        ...it,
        item_id: m.id,
        item_name: m.name,
        thickness: parsedThk || it.thickness,
        rate_per_sqft: jobWork ? rate : (rate > 0 ? rate : it.rate_per_sqft),
        unit: resolvedUnit,
        category: cat,
        // Auto-set per-row margin based on material (SS=6", MS=3")
        margin_inches: marginForItemName(m.name),
      };
      if (isHardware) {
        // Wipe cutting dimensions — irrelevant for ready-made items.
        next.width_in = "";
        next.height_in = "";
        next.sizing_mode = "exact";
        next.sizing_mode_w = "exact";
        next.sizing_mode_h = "exact";
      }
      return next;
    }));
  };
  // New rows default to per-row margin ON with the material-derived value.
  // Owner can override each row's margin (or turn it off entirely) inline.
  const addItem = () => setItems((p) => [...p, { ...blankItem(), sizing_mode: "standard" }]);
  const removeItem = (idx) => setItems((p) => p.filter((_, i) => i !== idx));

  const addRefImage = (files) => {
    const arr = Array.from(files || []).filter((f) => f.type.startsWith("image/"));
    if (arr.length) setRefImages((p) => [...p, ...arr]);
  };
  const removeRefImage = (idx) => setRefImages((p) => p.filter((_, i) => i !== idx));

  // Edit mode — delete an ALREADY-uploaded reference image server-side
  // and drop it from the visible strip.
  const removeExistingRef = async (imgId) => {
    if (!isEdit || !initialOrder?.id || !imgId) return;
    try {
      await api.delete(`/orders/${initialOrder.id}/reference-image/${imgId}`);
      setExistingRefs((prev) => prev.filter((r) => r.id !== imgId));
      toast.success("Image removed");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const refImgSrc = (imgId) => {
    if (!isEdit || !initialOrder?.id) return "";
    const tok = getAuthToken() || "";
    return `${API}/orders/${initialOrder.id}/reference-image/${imgId}?auth=${encodeURIComponent(tok)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (mobile.trim() && !normalizePhone(mobile)) { toast.error(INVALID_PHONE_MSG); return; }
    if (!customer.trim() || !mobile.trim()) {
      toast.error("Customer name and mobile are required");
      return;
    }
    const redLine = items.map((it, i) => {
      const l = totals.lines[i];
      return rateWarning(it, l ? (l.sqft > 0 ? l.net / l.sqft : l.mult > 0 ? l.net / l.mult : 0) : 0);
    }).find((w) => w?.level === "red");
    if (!isEstimate && !isEstimateEdit && redLine) { toast.error(redLine.text); return; }
    if (commV2 && commEnabled && canComm) {
      const bad = welderErrors(cLines, welderRates)[0];
      if (bad) { toast.error(`Below welder rate ₹${bad.floor}`); return; }
      if (!(welderAmt > 0)) { toast.error("Welder commission: kisi sheet line pe ₹/sqft bhariye"); return; }
    }

    // ---- EDIT MODE ------------------------------------------------------
    // Existing order → sequentially call PATCH endpoints. Advance payment
    // section is hidden in edit mode (handled by the ledger flow), and
    // Invoice No + Date are read-only (no PATCH endpoint for them).
    if (isEdit) {
      setBusy(true);
      try {
        // ── ESTIMATE EDIT ────────────────────────────────────────────
        // Estimates use a single PATCH /estimates/{id} with the full
        // body (backend recomputes totals). No advance/deadline/upload
        // pieces here — those only apply once converted to an order.
        if (isEstimateEdit) {
          const body = {
            customer_type: welderId ? "welder" : "one_time",
            welder_id: welderId || null,
            customer_name: customer.trim(),
            mobile: normalizePhone(mobile) || mobile.trim(),
            address: address.trim(),
            design_description: designDescription,
            project_type: projectType,
            notes: notes,
            items: items.map((it, idx) => {
              const su = it.size_unit || "inch";
              return {
                item_id: it.item_id || null,
                item_name: it.item_name || "",
                thickness: it.thickness,
                rate_per_sqft: Number(it.rate_per_sqft),
                width_in: toInches(it.width_in, su),
                height_in: toInches(it.height_in, su),
                quantity: Number(it.quantity),
                sizing_mode: it.sizing_mode,
                sizing_mode_w: it.sizing_mode_w ?? it.sizing_mode ?? "standard",
                sizing_mode_h: it.sizing_mode_h ?? it.sizing_mode ?? "standard",
                margin_inches: Number(it.margin_inches ?? 3),
                margin_w_inches: Number(it.margin_w_inches ?? it.margin_inches ?? 3),
                margin_h_inches: Number(it.margin_h_inches ?? it.margin_inches ?? 3),
                unit: (it.unit || "sqft").toLowerCase(),
                discount_percent: (it.discount_mode || "amount") === "percent"
                  ? Number(it.discount_percent) || 0
                  : 0,
                discount_per_sqft: it.discount_mode === "per_sqft" ? Number(it.discount_per_sqft) || 0 : 0,
                discount_amount: (it.discount_mode || "amount") === "amount" ? Number(it.discount_amount) || 0 : 0,
                size_unit: su,
              };
            }),
          };
          const { data: fresh } = await api.patch(`/estimates/${initialOrder.id}`, body);
          toast.success(`Estimate ${fresh.estimate_number || ""} updated`);
          onSaved?.(fresh);
          return;
        }

        // 1) Customer name / mobile / address
        await api.patch(`/orders/${initialOrder.id}/customer`, {
          customer_name: customer.trim(),
          mobile: normalizePhone(mobile) || mobile.trim(),
          address: address.trim(),
        });

        // 1b) Project type (Sheet Cutting ↔ Gate & Railing)
        const prevType = initialOrder.project_type || "sheet_cutting";
        if (projectType !== prevType) {
          await api.patch(`/orders/${initialOrder.id}/project-type`, { project_type: projectType });
        }
        if (projectType === "gate_railing" && (initialOrder.work_type || "gate") !== workType) {
          await api.patch(`/orders/${initialOrder.id}/work-type`, { work_type: workType });
        }
        if (projectType === "gate_railing" && fabricatorId
            && fabricatorId !== (initialOrder.assigned_fabricator_id || initialOrder.fabricator_id || "")) {
          try {
            await api.post(`/orders/${initialOrder.id}/assign-fabricator`, { fabricator_id: fabricatorId });
          } catch (_e) {
            toast.warning("Fabricator assign fail — Order detail se manually assign kariye");
          }
        }

        // 2) Items — backend blocks this if order is `delivered`. We surface
        //    that as a toast but continue with the other updates.
        try {
          await api.patch(`/orders/${initialOrder.id}/items`, {
            items: items.map((it, idx) => {
              const su = it.size_unit || "inch";
              const wIn = toInches(it.width_in, su);
              const hIn = toInches(it.height_in, su);
              return {
                item_id: it.item_id || null,
                fab_rate_id: it.fab_rate_id || null,
                work_item: it.work_item || null,
                item_name: it.item_name || "",
                thickness: it.thickness,
                rate_per_sqft: Number(it.rate_per_sqft),
                width_in: wIn,
                height_in: hIn,
                quantity: Number(it.quantity),
                sizing_mode: it.sizing_mode,
                sizing_mode_w: it.sizing_mode_w ?? it.sizing_mode ?? "standard",
                sizing_mode_h: it.sizing_mode_h ?? it.sizing_mode ?? "standard",
                margin_inches: Number(it.margin_inches ?? 3),
                margin_w_inches: Number(it.margin_w_inches ?? it.margin_inches ?? 3),
                margin_h_inches: Number(it.margin_h_inches ?? it.margin_inches ?? 3),
                unit: (it.unit || "sqft").toLowerCase(),
                discount_percent: (it.discount_mode || "amount") === "percent"
                  ? Number(it.discount_percent) || 0
                  : 0,
                discount_per_sqft: it.discount_mode === "per_sqft" ? Number(it.discount_per_sqft) || 0 : 0,
                discount_amount: (it.discount_mode || "amount") === "amount" ? Number(it.discount_amount) || 0 : 0,
                ...commRatePayload(idx),
                size_unit: su,
              };
            }),
            ...(canComm ? {
              allow_staff_commission: staffComm && !commEnabled,
              update_welder_commission: !welderPaid,
              welder_commission: commEnabled && welderAmt > 0 ? {
                welder_id: commWelderId || null, welder_name: commWelderName.trim(),
                welder_mobile: commWelderMobile.trim(), amount: welderAmt, note: commNote.trim(),
              } : null,
            } : {}),
          });
        } catch (err) {
          if ([400, 403].includes(err.response?.status)) throw err;
          const msg = formatApiError(err.response?.data?.detail) || err.message;
          toast.warning(`Sheets update skipped: ${msg}`);
        }

        // 3) Design description + notes
        await api.patch(`/orders/${initialOrder.id}/notes`, {
          design_description: designDescription,
          notes: notes,
        });

        // 4) Deadline (only meaningful for gate/railing)
        if (projectType === "gate_railing") {
          try {
            await api.patch(`/orders/${initialOrder.id}/deadline`, {
              deadline_date: deadlineDate || null,
            });
          } catch (_e) { /* ignore silently */ }
        }

        // 5) Upload any freshly-picked reference images
        for (const file of refImages) {
          try {
            const fd = new FormData();
            fd.append("kind", "reference_image");
            fd.append("file", file);
            await api.post(`/orders/${initialOrder.id}/upload`, fd, {
              headers: { "Content-Type": "multipart/form-data" },
            });
          } catch (_e) {
            toast.warning(`Failed to upload ${file.name}`);
          }
        }

        // Re-fetch to hand back the fresh order to the caller
        const { data: fresh } = await api.get(`/orders/${initialOrder.id}`);
        toast.success(`Order #${fresh.order_number} updated`);
        onSaved?.(fresh);
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail) || err.message);
      } finally {
        setBusy(false);
      }
      return;
    }

    // ---- CREATE MODE ----------------------------------------------------
    const isWelder = !!welderId;
    setBusy(true);
    try {
      let finalWelderId = welderId;
      // If user opted to save this new customer as a welder, create the welder first
      if (!isWelder && saveAsWelder) {
        try {
          const { data: newW } = await api.post("/welders", {
            name: customer.trim(),
            shop_name: shopName.trim(),
            mobile: normalizePhone(mobile) || mobile.trim(),
            address: address.trim(),
          });
          finalWelderId = newW.id;
        } catch (err) {
          toast.warning("Couldn't save as welder — proceeding as one-time");
        }
      }
      // Only pass `custom_order_number` if the user actually EDITED the field.
      // Otherwise the field just carries the auto-fetched preview value and we
      // must let the backend allocate a real (incremented) number — else the
      // counter never moves and the next order fails with "already exists".
      const _typed = customOrderNumber.trim();
      const _isCustom = _typed && _typed !== autoOrderNumber.trim();
      const payload = {
        customer_type: finalWelderId ? "welder" : "one_time",
        welder_id: finalWelderId || null,
        customer_name: customer.trim(),
        mobile: normalizePhone(mobile) || mobile.trim(),
        address: address.trim(),
        project_type: projectType,
        custom_order_number: _isCustom ? _typed : null,
        custom_created_at: customDate || null,
        design_description: designDescription.trim(),
        notes: notes.trim(),
        items: items.map((it, idx) => {
          // If the user typed sizes in mm, convert to inches for the API
          // payload — backend contract (width_in / height_in) stays inches.
          const su = it.size_unit || "inch";
          const wIn = toInches(it.width_in, su);
          const hIn = toInches(it.height_in, su);
          return {
            item_id: it.item_id || null,
            item_name: it.item_name || "",
            fab_rate_id: it.fab_rate_id || null,
            work_item: it.work_item || null,
            thickness: it.thickness,
            rate_per_sqft: Number(it.rate_per_sqft),
            width_in: wIn,
            height_in: hIn,
            quantity: Number(it.quantity),
            sizing_mode: it.sizing_mode,
            // Per-axis sizing modes (fall back to row-level `sizing_mode` if
            // the axis-specific one wasn't set on this row).
            sizing_mode_w: it.sizing_mode_w ?? it.sizing_mode ?? "standard",
            sizing_mode_h: it.sizing_mode_h ?? it.sizing_mode ?? "standard",
            // CRITICAL — send the user's chosen margins to the backend.
            // Without these fields the backend falls back to material defaults
            // (SS = 6", MS = 3"), which silently ignored the value the owner
            // typed in the "W (in)" / "H (in)" margin editors.
            margin_inches: Number(it.margin_inches ?? 3),
            margin_w_inches: Number(it.margin_w_inches ?? it.margin_inches ?? 3),
            margin_h_inches: Number(it.margin_h_inches ?? it.margin_inches ?? 3),
            unit: (it.unit || "sqft").toLowerCase(),
            discount_percent: (it.discount_mode || "amount") === "percent"
              ? Number(it.discount_percent) || 0
              : 0,
            discount_per_sqft: it.discount_mode === "per_sqft" ? Number(it.discount_per_sqft) || 0 : 0,
            discount_amount: (it.discount_mode || "amount") === "amount" ? Number(it.discount_amount) || 0 : 0,
            ...commRatePayload(idx),
            // Record what unit the operator entered (for audit / history),
            // backend can ignore this if not needed.
            size_unit: su,
          };
        }),
        advance_amount: advNum,
        advance_mode: advNum > 0 ? advanceMode : null,
        deadline_date: projectType === "gate_railing" ? (deadlineDate || null) : null,
        is_emergency: !isEstimate && !!isEmergency,
        billing_mode: projectType === "sheet_cutting" ? billingMode : "per_sqft",
        work_type: projectType === "gate_railing" ? workType : null,
        attach_spec: gateV2 ? attachSpec : undefined,
        wholesale_weighed_kg: billingMode === "wholesale" ? Number(wKg) || 0 : null,
        wholesale_rate_per_kg: billingMode === "wholesale" ? Number(wRate) || 0 : null,
        // Phase 3A: Sales GST customer snapshot (all optional)
        customer_gstin: custGstin.trim().toUpperCase() || null,
        customer_state: custState.trim() || null,
        customer_state_code: custStateCode.trim() || null,
        customer_gst_registered: !!custGstRegistered,
        advance_gst_hint: advNum > 0 ? advGstHint : null,
        allow_staff_commission: !isEstimate && canComm && staffComm && !commEnabled,
        welder_commission: (!isEstimate && canComm && commEnabled && welderAmt > 0)
          ? {
              welder_id: commWelderId || null,
              welder_name: commWelderName.trim(),
              welder_mobile: commWelderMobile.trim(),
              amount: welderAmt,
              note: commNote.trim(),
            }
          : null,
      };
      const url = isEstimate ? "/estimates" : "/orders";
      const { data } = await api.post(url, payload);

      // Estimates skip the post-create actions (no fabricator, no reference
      // image upload, no advance handling) — they're pending quotes. All
      // that stuff kicks in once the estimate is "Convert to Order"-ed.
      if (!isEstimate) {
        // Assign fabricator (only relevant for gate/railing) if picked
        if (projectType === "gate_railing" && fabricatorId) {
          try {
            await api.post(`/orders/${data.id}/assign-fabricator`, { fabricator_id: fabricatorId });
          } catch (_e) {
            toast.warning("Order create ho gaya but fabricator assign fail — Order detail se manually assign kariye");
          }
        }

        // Upload reference images sequentially (usually 1-3)
        for (const file of refImages) {
          try {
            const fd = new FormData();
            fd.append("kind", "reference_image");
            fd.append("file", file);
            await api.post(`/orders/${data.id}/upload`, fd, {
              headers: { "Content-Type": "multipart/form-data" },
            });
          } catch (_e) {
            toast.warning(`Failed to upload ${file.name}`);
          }
        }
      }

      toast.success(
        isEstimate
          ? `Estimate ${data.order_number || data.id} saved`
          : `Order ${data.order_number} created`,
      );
      setCustomer(""); setMobile(""); setAddress(""); setDesignDescription(""); setNotes("");
      setProjectType("sheet_cutting");
      setBillingMode("per_sqft"); setWKg(""); setWRate("");
      setWelderId("");
      setFabricatorId("");
      setDeadlineDate("");
      setItems([blankItem()]);
      setAdvanceAmount("");
      setRefImages([]);
      // Re-fetch next order number and reset date to today (auto-fill for next order)
      setCustomDate(istToday());
      try {
        const { data: nn } = await api.get("/orders/next-number");
        const nextNo = nn?.next_order_number || "";
        setCustomOrderNumber(nextNo);
        setAutoOrderNumber(nextNo);
      } catch (_e) { setCustomOrderNumber(""); setAutoOrderNumber(""); }
      onCreated?.(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="order-intake-form">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-3">
        <div>
          {onBack && !isEdit && (
            <button type="button" onClick={onBack} className="text-sm text-slate-400 hover:text-slate-100 mb-1" data-testid="new-order-back">← Orders</button>
          )}
          <h1 className="text-3xl font-semibold tracking-tight text-slate-50" data-testid="new-order-title">
            {isEdit
              ? (isEstimateEdit
                  ? `Edit estimate ${initialOrder?.estimate_number || initialOrder?.order_number || ""}`
                  : `Edit order #${initialOrder?.order_number || ""}`)
              : (isEstimate ? "Estimate" : "New order")}
          </h1>
        </div>
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-1.5 text-sm text-slate-400">
            <span>Invoice</span>
            <input
              value={customOrderNumber}
              onChange={(e) => setCustomOrderNumber(e.target.value.toUpperCase().slice(0, 40))}
              placeholder="AUTO"
              readOnly={isEdit}
              className="w-36 bg-transparent border-0 border-b border-dashed border-slate-600 font-mono text-slate-100 focus:outline-none focus:border-orange-500 px-0.5"
              data-testid="new-order-custom-number"
            />
            <span>·</span>
            <input
              type="date"
              value={customDate}
              max={istToday()}
              onChange={(e) => setCustomDate(e.target.value)}
              readOnly={isEdit}
              className="bg-transparent text-slate-300 focus:outline-none"
              data-testid="new-order-custom-date"
            />
          </div>
          <div className="inline-flex p-1 rounded-sm bg-slate-700/50 border border-slate-700/60">
            {[["sheet_cutting", "Sheet cutting", "project-type-sheet"], ["gate_railing", "Gates & railings", "project-type-gate"]].map(([k, l, tid]) => (
              <button key={k} type="button" onClick={() => setProjectType(k)} data-testid={tid}
                className={"px-4 py-1.5 text-sm rounded-sm transition-colors " +
                  (projectType === k ? "bg-slate-800 text-slate-50 font-medium shadow-sm" : "text-slate-400 hover:text-slate-200")}>
                {l}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_360px] gap-5 items-start">
      <div className="space-y-5 min-w-0">
      {/* Fabrication department assignment — only relevant for gate/railing
          orders. Picking a fabricator here means they see this order in
          their `/fabrication` dashboard the moment it's created. */}
      {projectType === "gate_railing" && !gateV2 && (
        <WorkTypePicker value={workType} onChange={setWorkType} />
      )}
      {projectType === "gate_railing" && !gateV2 && (
        <div className="surface p-6" data-testid="fabricator-assign-block">
          <Label className="text-cyan-300 font-mono-num text-[10px] tracking-widest">
            Fabricator
          </Label>
          <div className="mt-2 flex flex-col md:flex-row gap-3 items-stretch">
            <Select value={fabricatorId || "none"} onValueChange={(v) => setFabricatorId(v === "none" ? "" : v)}>
              <SelectTrigger className="bg-slate-800/60 border-slate-600 text-white h-11 rounded-sm flex-1" data-testid="fabricator-select">
                <SelectValue placeholder="Fabricator chuno…" />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-600 text-white">
                <SelectItem value="none">— No fabricator (assign later) —</SelectItem>
                {fabricators.map((f) => (
                  <SelectItem key={f.id} value={f.id}>
                    {f.name} · {f.email || f.mobile || "—"}
                    {f.open_orders_count ? ` · ${f.open_orders_count} open` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {fabricators.length === 0 && (
            <div className="text-[11px] text-amber-300 mt-2">
              Abhi tak koi fabricator user nahi hai — <b>Settings → Users</b> me jaake role <b>"fabrication"</b> se user add karo.
            </div>
          )}

          {/* Internal deadline — ADMIN + FABRICATOR only. Hidden from
              customer track page. Helps owner track promised delivery date. */}
          <div className="mt-3 pt-3 border-t border-slate-700/60 flex flex-col md:flex-row md:items-end gap-3">
            <div className="flex-1">
              <Label className="text-amber-300 font-mono-num text-[10px] tracking-widest">
                Internal deadline
              </Label>
              <input
                type="date"
                value={deadlineDate}
                onChange={(e) => setDeadlineDate(e.target.value)}
                min={istToday()}
                className="bg-slate-800/60 border border-amber-500/40 text-amber-100 h-11 rounded-sm mt-1.5 font-mono-num px-3 w-full"
                data-testid="new-order-deadline"
              />
            </div>
          </div>
        </div>
      )}

      {/* ============================================================
          ROW · Customer info + Invoice (side-by-side within one panel)
          Layout: LEFT (customer name + phone in one row, address below)
                  RIGHT (invoice no on top, invoice date below)
      ============================================================ */}
      <div className="surface p-6" data-testid="customer-card">
        <div className="font-semibold text-slate-100 mb-4">Customer</div>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Phone number">
                <div className="relative">
                  <Input
                    data-testid="new-order-mobile-input"
                    value={mobile}
                    onChange={(e) => setMobile(e.target.value)}
                    onBlur={() => setMobile(cleanPhoneInput(mobile))}
                    onPaste={(e) => { const n = normalizePhone(e.clipboardData.getData("text")); if (n) { e.preventDefault(); setMobile(n); } }}
                    placeholder="10-digit mobile…"
                    inputMode="tel"
                    className={"bg-slate-800/60 text-white h-11 rounded-sm font-mono-num " + (mobile.trim() && !normalizePhone(mobile) ? "border-red-500" : "border-slate-600")}
                    required
                  />
                </div>
                {mobile.trim() && !normalizePhone(mobile) ? (
                  <div className="text-[11px] text-red-400 mt-1" data-testid="new-order-mobile-error">{INVALID_PHONE_MSG}</div>
                ) : null}
              </Field>
              <Field label="Customer name">
                <div className="relative">
                  <Input
                    data-testid="new-order-customer-input"
                    value={customer}
                    onChange={(e) => { setCustomer(e.target.value); setShowNameSuggest(true); }}
                    onFocus={() => setShowNameSuggest(true)}
                    onBlur={() => setTimeout(() => setShowNameSuggest(false), 180)}
                    placeholder="Customer name…"
                    className="bg-slate-800/60 border-slate-600 text-white h-11 rounded-sm"
                    required
                    autoComplete="off"
                  />
                  {showNameSuggest && nameSuggestions.length > 0 && (
                    <div
                      className="absolute z-30 left-0 right-0 mt-1 bg-slate-900 border border-slate-600 rounded-sm shadow-2xl max-h-64 overflow-y-auto"
                      data-testid="name-suggestions"
                    >
                      {nameSuggestions.map((w) => (
                        <button
                          key={w.id}
                          type="button"
                          onMouseDown={(e) => { e.preventDefault(); pickWelder(w); }}
                          className="w-full text-left px-3 py-2 hover:bg-slate-800 border-b border-slate-800 last:border-b-0 flex items-center justify-between gap-3"
                          data-testid={`name-suggest-${w.id}`}
                        >
                          <div className="flex flex-col min-w-0">
                            <span className="text-slate-200 truncate">{w.name}</span>
                            {w.shop_name && (
                              <span className="text-[10px] text-slate-500 truncate">{w.shop_name}</span>
                            )}
                          </div>
                          <span className="text-cyan-300 font-mono-num text-xs whitespace-nowrap">{w.mobile}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </Field>
            </div>
            {existingCustomer && !welderId && (
              <div className="rounded-sm border border-emerald-500/50 bg-emerald-500/10 px-3 py-2 space-y-2 text-emerald-700" data-testid="existing-customer-chip">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold bg-emerald-500/20 px-1.5 py-0.5 rounded-sm" data-testid="existing-customer-found">Existing Customer Found</span>
                  <span className="text-sm flex-1 truncate">
                    <b>{(existingCustomer.candidates || []).find((c) => c.customer_name.toLowerCase() === customer.trim().toLowerCase())?.customer_name || existingCustomer.customer_name}</b>
                    <span className="text-xs"> · {existingCustomer.order_count} order{existingCustomer.order_count === 1 ? "" : "s"}</span>
                  </span>
                </div>
                {(existingCustomer.candidates || []).length > 1 && (
                  <div className="flex flex-wrap gap-1.5 items-center" data-testid="existing-customer-candidates">
                    <span className="text-xs">Is number par naam:</span>
                    {existingCustomer.candidates.map((c, i) => (
                      <button key={c.customer_name} type="button" data-testid={`existing-candidate-${i}`}
                        onClick={() => { setCustomer(c.customer_name); if (c.address) setAddress(c.address); }}
                        className={"text-xs px-2 py-0.5 rounded-sm border " + (customer.trim().toLowerCase() === c.customer_name.toLowerCase() ? "bg-emerald-600 text-white border-emerald-600" : "border-emerald-500/50 hover:bg-emerald-500/15")}>
                        {c.customer_name}
                      </button>
                    ))}
                  </div>
                )}
                {(existingCustomer.recent_orders || []).length > 0 && (
                  <div className="text-xs font-mono-num flex flex-wrap gap-x-3" data-testid="existing-customer-recent">
                    {existingCustomer.recent_orders.map((o) => (
                      <span key={o.id}>{o.order_number} · {new Date(o.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", timeZone: "Asia/Kolkata" })}</span>
                    ))}
                  </div>
                )}
              </div>
            )}
            <Field label="Billing address">
              <div className="relative">
                <textarea
                  data-testid="new-order-address-input"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder="Delivery / pickup address, city, landmark…"
                  rows={2}
                  className="w-full px-3 py-2.5 bg-slate-800/60 border border-slate-600 text-white text-sm rounded-sm resize-none focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500"
                />
              </div>
            </Field>

            {/* Phase 3A: GST section — only shown when business has GST on + go-live set */}
            {gstEnabled && (
              <div className="border border-cyan-500/30 rounded-sm bg-cyan-900/10 p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="text-[10px] font-mono-num tracking-widest text-cyan-300">
                    GST / TAX INVOICE DETAILS
                  </div>
                  <div className="text-[10px] text-slate-500">
                    Biz state: <span className="text-slate-300">{bizGst?.state} ({bizGst?.state_code})</span>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <label className="flex items-center gap-2 cursor-pointer text-xs text-slate-200">
                    <input type="checkbox" checked={custGstRegistered}
                      onChange={(e) => setCustGstRegistered(e.target.checked)}
                      className="accent-cyan-500"
                      data-testid="order-gst-registered-cb" />
                    Customer is GST-registered (B2B)
                  </label>
                  <Field label="Customer GSTIN">
                    <Input value={custGstin} onChange={(e) => setCustGstin(e.target.value)}
                      placeholder="15-char GSTIN — optional for B2C"
                      className="bg-slate-800/60 border-slate-600 text-white h-9 font-mono-num uppercase"
                      data-testid="order-gstin-input" />
                  </Field>
                  <Field label="Customer State">
                    <Input value={custState} onChange={(e) => setCustState(e.target.value)}
                      placeholder="e.g. Bihar"
                      className="bg-slate-800/60 border-slate-600 text-white h-9"
                      data-testid="order-cust-state" />
                  </Field>
                  <Field label="State Code">
                    <Input value={custStateCode} onChange={(e) => setCustStateCode(e.target.value)}
                      placeholder="e.g. 10"
                      className="bg-slate-800/60 border-slate-600 text-white h-9 font-mono-num"
                      data-testid="order-cust-state-code" />
                  </Field>
                </div>
                <div className="text-[10px] text-amber-300/80 pt-1">
                  {custStateCode && bizGst?.state_code && custStateCode !== bizGst.state_code
                    ? `→ Interstate (IGST @ item's GST %)`
                    : `→ Intra-state (CGST + SGST split)`}
                </div>
              </div>
            )}
            {welderId ? (
              <div className="flex items-center gap-2 text-[11px] font-mono-num text-emerald-400 bg-emerald-500/10 border border-emerald-500/40 px-3 py-1.5 rounded-sm">
                <User size={12}/>
                <span>REGULAR CUSTOMER — auto-fetched</span>
              </div>
            ) : (!isEdit && mobile.trim()) && (
              <div className="space-y-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={saveAsWelder}
                    onChange={(e) => setSaveAsWelder(e.target.checked)}
                    className="accent-orange-500"
                    data-testid="save-welder-checkbox"
                  />
                  <span className="text-xs text-slate-300">
                    Save as <b className="text-orange-400">wholesale/welder</b> — auto-fetch next time
                  </span>
                </label>
                {saveAsWelder && (
                  <Field label="Shop Name (optional)">
                    <Input
                      value={shopName}
                      onChange={(e) => setShopName(e.target.value)}
                      placeholder="e.g., Sharma Fabrication Works"
                      className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm"
                    />
                  </Field>
                )}
              </div>
            )}
          </div>

      </div>

      {/* ============================================================
          ROW · Reference Images (dynamic slots + add-more) + Design Description
          Multiple reference images supported — click + card to add more.
      ============================================================ */}
      <div className="surface p-6" data-testid="design-card">
        <div className="font-semibold text-slate-100 mb-4">Design</div>
        <div className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-4">
          {/* Reference images — existing files + a trailing "+" slot */}
          <div className="flex flex-wrap gap-3" data-testid="ref-images-grid">
            {/* Already-uploaded server-side images (edit mode) */}
            {isEdit && existingRefs.map((r) => (
              <div
                key={r.id}
                className="relative w-40 md:w-44 aspect-square rounded-sm border-2 border-cyan-500/60 bg-slate-800/40 overflow-hidden"
                data-testid={`existing-ref-${r.id}`}
              >
                <img
                  src={refImgSrc(r.id)}
                  alt={r.filename || "Reference"}
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-1.5 left-1.5 bg-slate-900/85 rounded-sm px-1.5 py-0.5 text-[10px] font-mono-num text-cyan-200">
                  SAVED
                </div>
                <button
                  type="button"
                  onClick={() => removeExistingRef(r.id)}
                  className="absolute top-1.5 right-1.5 bg-slate-900/85 hover:bg-red-500 rounded-sm p-1"
                  data-testid={`existing-ref-remove-${r.id}`}
                  title="Remove saved image"
                >
                  <X size={14} className="text-white" />
                </button>
              </div>
            ))}
            {refImages.map((file, idx) => (
              <div
                key={idx}
                className="relative w-40 md:w-44 aspect-square rounded-sm border-2 border-orange-500/60 bg-slate-800/40 overflow-hidden"
                data-testid={`ref-slot-${idx}`}
              >
                <img
                  src={URL.createObjectURL(file)}
                  alt={`Reference ${idx + 1}`}
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-1.5 left-1.5 bg-slate-900/85 rounded-sm px-1.5 py-0.5 text-[10px] font-mono-num text-slate-200">
                  {idx + 1}
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setRefImages((prev) => prev.filter((_, i) => i !== idx));
                  }}
                  className="absolute top-1.5 right-1.5 bg-slate-900/85 hover:bg-red-500 rounded-sm p-1"
                  data-testid={`ref-slot-remove-${idx}`}
                  title="Remove"
                >
                  <X size={14} className="text-white" />
                </button>
              </div>
            ))}
            {/* trailing add-more slot */}
            <label
              className="relative w-40 md:w-44 aspect-square rounded-sm border-2 border-dashed border-slate-600 bg-slate-800/40 hover:border-orange-400/60 hover:bg-slate-800/60 flex items-center justify-center cursor-pointer overflow-hidden transition-colors"
              data-testid="ref-slot-add"
            >
              <input
                type="file"
                hidden
                accept="image/*"
                multiple
                onChange={(e) => {
                  const files = Array.from(e.target.files || []);
                  if (files.length === 0) return;
                  setRefImages((prev) => [...prev, ...files]);
                  e.target.value = "";
                }}
                data-testid="ref-slot-add-input"
              />
              <div className="flex flex-col items-center gap-2 text-slate-500 px-3">
                <ImageSquare size={22} />
                <div className="text-sm text-slate-400 text-center">
                  {(refImages.length === 0 && existingRefs.length === 0) ? "Add image" : "Add more"}
                </div>
              </div>
            </label>
          </div>

          {/* Design description */}
          <div className="flex flex-col">
            <div className="text-sm font-medium text-slate-300 mb-1.5">Description</div>
            <textarea
              data-testid="design-description-input"
              value={designDescription}
              onChange={(e) => setDesignDescription(e.target.value)}
              placeholder="Add image description here — colour, finish, corners, extra text/logo, delivery preference, etc."
              className="flex-1 min-h-[150px] w-full px-3 py-2 bg-slate-800/60 border border-slate-600 text-white text-sm rounded-sm resize-none focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500"
            />
          </div>
        </div>
      </div>

      {/* Items table (Vyapar-style) */}
      <div className="surface overflow-hidden" data-testid="sheets-card">
        <div className="flex items-start justify-between flex-wrap gap-3 px-6 pt-5 pb-4">
          <div className="font-semibold text-slate-100">
            <span data-testid="matrix-heading">{projectType === "gate_railing"
              ? `${(WORK_TYPES.find((w) => w.key === workType) || WORK_TYPES[0]).label} size`
              : "Sheets"}</span>
          </div>
          {itemsError && (
            <span className="flex items-center gap-2 text-[11px] text-red-400" data-testid="items-load-error">
              {itemsError}
              <button type="button" onClick={loadItems} className="px-2 py-0.5 border border-red-500/50 rounded-sm text-red-300 hover:bg-red-500/10" data-testid="items-retry-btn">RETRY</button>
            </span>
          )}
          {!listsLoaded && !itemsError && (
            <span className="flex items-center gap-2 text-[11px] text-slate-400" data-testid="items-loading">
              <span className="w-3 h-3 border-2 border-orange-400 border-t-transparent rounded-full animate-spin" /> Items load ho rahe hain…
            </span>
          )}
          {projectType === "sheet_cutting" && (
            <BillingModeBar mode={billingMode} setMode={changeBillingMode} disabled={isEdit}
              wKg={wKg} setWKg={setWKg} wRate={wRate} setWRate={setWRate}
              items={items} materialItems={materialItems} isAdmin={isAdmin} />
          )}
        </div>

        {gateV2 && (
          <div className="mb-4 px-6">
            <GateWorkItems items={items} setItems={setItems} rateRows={fabRates.rows} columns={fabRates.columns} />
            <div className="text-sm font-medium text-slate-300 mt-5">Hardware</div>
          </div>
        )}
        <div className="overflow-x-auto relative">
          <table className="w-full text-sm" data-testid="items-table">
            <thead>
              <tr className="text-xs font-normal text-slate-500 border-b border-slate-700/70">
                <th className="text-left font-normal pl-6 pr-2 py-2 w-10">#</th>
                <th className="text-left font-normal px-3 py-2 min-w-[200px]">Item</th>
                <th className="text-left font-normal px-2 py-2 min-w-[190px]">Width × Height</th>
                <th className="text-center font-normal px-2 py-2 w-20">Qty</th>
                <th className="text-left font-normal px-2 py-2 w-24">Unit</th>
                {billingMode !== "wholesale" && <th className="text-right font-normal px-2 py-2 w-24">Rate</th>}
                {billingMode !== "wholesale" && <th className="text-right font-normal px-2 py-2 min-w-[140px]">Discount</th>}
                <th className="text-right font-normal px-3 py-2 w-28">Amount</th>
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, idx) => {
                if (it.work_item) return null;
                const unit = (it.unit || "sqft").toLowerCase();
                const isSqft = unit === "sqft";
                // Per-axis margins + sizing modes (falls back to the legacy
                // single `margin_inches` / `sizing_mode` for older rows).
                const mW = Number(it.margin_w_inches ?? it.margin_inches ?? 3) || 0;
                const mH = Number(it.margin_h_inches ?? it.margin_inches ?? 3) || 0;
                const modeW = (it.sizing_mode_w ?? it.sizing_mode) || "standard";
                const modeH = (it.sizing_mode_h ?? it.sizing_mode) || "standard";
                // Convert the raw input to inches based on the row's size_unit.
                const su = it.size_unit || "inch";
                const wInRaw = toInches(it.width_in, su);
                const hInRaw = toInches(it.height_in, su);
                const wBilled = modeW === "standard" ? snapMargin(wInRaw, mW) : wInRaw;
                const hBilled = modeH === "standard" ? snapMargin(hInRaw, mH) : hInRaw;
                const qty = Number(it.quantity) || 0;
                const rate = Number(it.rate_per_sqft) || 0;
                const discMode = it.discount_mode || "amount";
                const rowSqft = isSqft ? (wBilled * hBilled * qty / 144) : 0;
                const gross = isSqft ? rowSqft * rate : qty * rate;
                const effDisc = lineDiscount(it, gross, rowSqft);
                const rowWarn = rateWarning(it, rowSqft > 0 ? (gross - effDisc) / rowSqft : qty > 0 ? (gross - effDisc) / qty : 0);
                const amt = billingMode === "wholesale"
                  ? (isSqft && totals.sqft > 0 ? ((wBilled * hBilled * qty) / 144 / totals.sqft) * totals.amount : 0)
                  : Math.max(gross - effDisc, 0);
                // "Bumped" = billed size differs from what user typed (converted to inches).
                const bumped = isSqft &&
                  ((modeW === "standard" && Math.abs(wBilled - wInRaw) > 0.001) ||
                   (modeH === "standard" && Math.abs(hBilled - hInRaw) > 0.001));
                return (
                  <tr
                    key={idx}
                    className="border-b border-slate-800 hover:bg-slate-800/30 align-top"
                    data-testid={`order-item-${idx}`}
                  >
                    <td className="px-3 py-2 font-mono-num text-slate-400">{idx + 1}</td>
                    <td className="px-3 py-2">
                      <ItemPicker
                        value={it.item_id || ""}
                        onChange={(v) => pickItem(idx, v)}
                        items={orderPickerItems(materialItems, it.item_id)}
                        placeholder={listsLoaded ? "Select item…" : itemsError ? "Items load fail — Retry dabayein" : "Loading items…"}
                        fallbackLabel={it.item_id ? `${it.item_name || "Item"}${listsLoaded ? "" : " …"}` : ""}
                        testid={`item-select-${idx}`}
                        loading={!listsLoaded}
                        error={itemsError}
                        onRetry={loadItems}
                      />
                      {it.item_name && (
                        <div className="text-[10px] text-slate-500 mt-1 truncate" title={it.item_name}>
                          {it.item_name}
                        </div>
                      )}
                    </td>
                    <td className="px-2 py-2">
                      {isSqft ? (
                        <div className="space-y-1.5">
                          <div className="flex items-start gap-1.5">
                            {/* W input + its own margin toggle */}
                            <div className="flex flex-col items-center gap-1">
                              <Input
                                type="number" step="0.01" min="0"
                                value={it.width_in}
                                onChange={(e) => updateItem(idx, "width_in", e.target.value)}
                                className="bg-slate-800/60 border-slate-600 text-white h-9 rounded-sm font-mono-num text-sm w-20"
                                data-testid={`width-input-${idx}`}
                                placeholder="W"
                              />
                              <div className="flex items-center gap-1">
                                <Switch
                                  checked={((it.sizing_mode_w ?? it.sizing_mode) || "standard") === "standard"}
                                  onCheckedChange={(v) => updateItem(idx, "sizing_mode_w", v ? "standard" : "exact")}
                                  data-testid={`row-margin-w-toggle-${idx}`}
                                  className="scale-75"
                                />
                                <Input
                                  type="number" min="0" step="1"
                                  value={it.margin_w_inches ?? it.margin_inches ?? 3}
                                  onChange={(e) => updateItem(idx, "margin_w_inches", Number(e.target.value) || 0)}
                                  disabled={((it.sizing_mode_w ?? it.sizing_mode) || "standard") !== "standard"}
                                  className="bg-slate-800/60 border-slate-600 text-white h-6 rounded-sm font-mono-num text-center w-10 px-1 no-spinner text-[11px] disabled:opacity-40"
                                  data-testid={`row-margin-w-input-${idx}`}
                                />
                                <span className="text-[9px] text-slate-500">″</span>
                              </div>
                            </div>
                            <span className="text-slate-500 mt-2">×</span>
                            {/* H input + its own margin toggle */}
                            <div className="flex flex-col items-center gap-1">
                              <Input
                                type="number" step="0.01" min="0"
                                value={it.height_in}
                                onChange={(e) => updateItem(idx, "height_in", e.target.value)}
                                className="bg-slate-800/60 border-slate-600 text-white h-9 rounded-sm font-mono-num text-sm w-20"
                                data-testid={`height-input-${idx}`}
                                placeholder="H"
                              />
                              <div className="flex items-center gap-1">
                                <Switch
                                  checked={((it.sizing_mode_h ?? it.sizing_mode) || "standard") === "standard"}
                                  onCheckedChange={(v) => updateItem(idx, "sizing_mode_h", v ? "standard" : "exact")}
                                  data-testid={`row-margin-h-toggle-${idx}`}
                                  className="scale-75"
                                />
                                <Input
                                  type="number" min="0" step="1"
                                  value={it.margin_h_inches ?? it.margin_inches ?? 3}
                                  onChange={(e) => updateItem(idx, "margin_h_inches", Number(e.target.value) || 0)}
                                  disabled={((it.sizing_mode_h ?? it.sizing_mode) || "standard") !== "standard"}
                                  className="bg-slate-800/60 border-slate-600 text-white h-6 rounded-sm font-mono-num text-center w-10 px-1 no-spinner text-[11px] disabled:opacity-40"
                                  data-testid={`row-margin-h-input-${idx}`}
                                />
                                <span className="text-[9px] text-slate-500">″</span>
                              </div>
                            </div>
                            {/* Compact unit pill — click to flip inch↔mm AND
                                convert existing W/H values. Sits where the
                                static "in" label used to live so column
                                width stays tight. */}
                            <button
                              type="button"
                              onClick={() => toggleRowUnit(idx)}
                              title={su === "inch" ? "Click to switch to MM (converts values)" : "Click to switch to INCH (converts values)"}
                              className={
                                "mt-1.5 h-7 px-2 rounded-sm border font-mono-num text-[10px] tracking-widest transition-colors " +
                                (su === "inch"
                                  ? "border-orange-500/60 bg-orange-500/10 text-orange-300 hover:bg-orange-500/20"
                                  : "border-cyan-500/60 bg-cyan-500/10 text-cyan-300 hover:bg-cyan-500/20")
                              }
                              data-testid={`size-unit-toggle-${idx}`}
                            >
                              {su === "inch" ? "INCH" : "MM"}
                            </button>
                          </div>
                          {/* Live mm → inch conversion hint */}
                          {su === "mm" && (Number(it.width_in) > 0 || Number(it.height_in) > 0) && (
                            <div className="text-[10px] font-mono-num text-cyan-300/80" data-testid={`mm-to-inch-hint-${idx}`}>
                              ≈ {round2(wInRaw)}″ × {round2(hInRaw)}″
                            </div>
                          )}
                          {bumped && (
                            <div className="text-[10px] font-mono-num text-orange-400" data-testid={`billed-size-${idx}`}>
                              → billed {round2(wBilled)}″ × {round2(hBilled)}″
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-slate-500 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-2 py-2">
                      <Input
                        type="number" step="1" min="1"
                        value={it.quantity}
                        onChange={(e) => updateItem(idx, "quantity", e.target.value)}
                        className="bg-slate-800/60 border-slate-600 text-white h-9 rounded-sm font-mono-num text-center w-full min-w-[64px] px-2 no-spinner"
                        data-testid={`qty-input-${idx}`}
                      />
                    </td>
                    <td className="px-2 py-2">
                      <Select value={unit} onValueChange={(v) => updateItem(idx, "unit", v)}>
                        <SelectTrigger className="bg-slate-800/60 border-slate-600 text-white h-9 rounded-sm text-xs" data-testid={`unit-select-${idx}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-900 border-slate-600 text-white">
                          <SelectItem value="sqft">sqft</SelectItem>
                          <SelectItem value="piece">piece</SelectItem>
                          <SelectItem value="set">set</SelectItem>
                          <SelectItem value="meter">meter</SelectItem>
                          <SelectItem value="ft">ft</SelectItem>
                          <SelectItem value="kg">kg</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                    {billingMode !== "wholesale" && (<>
                    <td className="px-2 py-2 text-right">
                      <Input
                        type="number" step="0.01" min="0"
                        value={it.rate_per_sqft}
                        onChange={(e) => updateItem(idx, "rate_per_sqft", e.target.value)}
                        className="bg-slate-800/60 border-slate-600 text-white h-9 rounded-sm font-mono-num text-right no-spinner px-2 min-w-[88px]"
                        data-testid={`rate-input-${idx}`}
                      />
                    </td>
                    <td className="px-2 py-2 text-right">
                      <div className="flex items-stretch gap-1" data-testid={`discount-cell-${idx}`}>
                        <button
                          type="button"
                          onClick={() => {
                            let nx = DISC_NEXT[discMode];
                            if (nx === "per_sqft" && !isSqft) nx = "amount";
                            updateItem(idx, "discount_mode", nx);
                          }}
                          className={`shrink-0 min-w-[2.5rem] px-1 h-9 rounded-sm font-mono-num text-xs font-black border ${
                            discMode !== "amount"
                              ? "bg-amber-500/20 text-amber-200 border-amber-500/50"
                              : "bg-slate-800/60 text-slate-300 border-slate-600 hover:bg-slate-700"
                          }`}
                          title="Discount type: ₹ flat → % → ₹ per sqft"
                          data-testid={`discount-mode-toggle-${idx}`}
                        >
                          {DISC_LABEL[discMode]}
                        </button>
                        <Input
                          type="number" step="0.01" min="0"
                          value={it[DISC_FIELD[discMode]] ?? 0}
                          onChange={(e) => updateItem(idx, DISC_FIELD[discMode], e.target.value)}
                          placeholder="0"
                          className="bg-slate-800/60 border-amber-500/30 text-amber-200 placeholder:text-slate-600 h-9 rounded-sm font-mono-num text-right no-spinner px-2 min-w-[5.5rem] w-full"
                          data-testid={`row-discount-${idx}`}
                        />
                      </div>
                    </td>
                    </>)}
                    <td className="px-3 py-2 text-right">
                      <div className="font-mono-num text-orange-400 font-bold" data-testid={`row-amount-${idx}`}>
                        ₹{round2(amt).toLocaleString("en-IN")}
                      </div>
                      {discMode !== "amount" && effDisc > 0 && (
                        <div className="text-[10px] text-amber-400 font-mono-num" data-testid={`row-discount-calc-${idx}`}>− ₹{round2(effDisc).toLocaleString("en-IN")} disc</div>
                      )}
                      {rowWarn && (
                        <div className={"text-[10px] font-bold max-w-[11rem] ml-auto " + (rowWarn.level === "red" ? "text-red-400" : "text-yellow-300")} data-testid={`row-rate-warning-${idx}`}>{rowWarn.text}</div>
                      )}
                      {isSqft && (
                        <div className="text-[10px] text-slate-500 font-mono-num">
                          {round3(isSqft ? (wBilled * hBilled * qty / 144) : 0)} sqft
                        </div>
                      )}
                    </td>
                    <td className="px-2 py-2 text-center">
                      {items.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeItem(idx)}
                          className="text-slate-500 hover:text-red-400 p-1"
                          data-testid={`remove-item-${idx}`}
                          title="Remove row"
                        >
                          <Trash size={16} />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="border-t border-slate-700/70">
                <td colSpan={2} className="pl-6 pr-3 py-4">
                  <Button
                    type="button"
                    onClick={addItem}
                    variant="outline"
                    size="sm"
                    className="rounded-sm border-slate-700 bg-slate-800 text-slate-100 hover:bg-slate-700/60 h-9 font-medium"
                    data-testid="add-item-button"
                  >
                    <Plus size={14} className="mr-1" /> {gateV2 ? "Add hardware" : "Add sheet"}
                  </Button>
                </td>
                <td colSpan={2} className="px-2 py-2 text-right text-sm text-slate-400">
                  Total
                </td>
                <td className="px-2 py-2 text-left font-semibold text-slate-100 text-sm">
                  {totals.sqft} sqft
                </td>
                <td colSpan={2}></td>
                <td className="px-3 py-2 text-right font-semibold text-slate-50 text-base" data-testid="table-total">
                  ₹{totals.amount.toLocaleString("en-IN")}
                </td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Commission toggles — Admin / Secondary Admin only, hidden in Estimate mode */}
      {!isEstimate && !isEstimateEdit && canComm && (
        <div className="surface p-6 space-y-5"
             data-testid="welder-commission-section">
          <div className="flex items-center gap-3">
            <Switch checked={staffComm} disabled={welderPaid || !staffPossible}
              onCheckedChange={(v) => { setStaffComm(v); if (v) setCommEnabled(false); }}
              data-testid="staff-commission-toggle" />
            <div>
              <div className="text-[15px] font-medium text-slate-100">
                Staff commission <span className="font-mono-num text-slate-400 font-normal ml-1" data-testid="staff-commission-auto">₹{(staffComm ? (commV2 ? staffAmt : autoComm) : 0).toLocaleString("en-IN")}</span>
              </div>
            </div>
          </div>
          {commV2 && staffComm && (
            <div className="ml-12" data-testid="staff-comm-box">
              <CommissionLines kind="staff" lines={cLines} rates={staffRates} editable={isAdmin}
                pending={!isAdmin && !(isEdit && initialOrder?.staff_commission?.status === "approved")}
                setRate={(i, v) => setStaffRates((p) => ({ ...p, [i]: v }))} />
            </div>
          )}
          <div className="flex items-center gap-3">
            <Switch checked={commEnabled} disabled={welderPaid}
              onCheckedChange={(v) => {
                setCommEnabled(v); setCommManual(false);
                if (v) setStaffComm(false);
                else { setCommWelderId(""); setCommWelderName(""); setCommWelderMobile(""); setCommAmount(""); setCommNote(""); setCommSearch(""); }
              }}
              data-testid="commission-toggle" />
            <div>
              <div className="text-[15px] font-medium text-slate-100">Welder commission</div>
              {welderPaid && <div className="text-xs text-amber-500 mt-0.5">Commission PAID hai — Welder Commissions page se Undo karo.</div>}
            </div>
          </div>

          {commEnabled && (
            <div className="mt-4 space-y-3">
              {/* Welder picker — type to search over existing welders */}
              <div className="relative">
                <Label className="text-[10px] text-slate-400 tracking-widest">WELDER</Label>
                <Input
                  value={commSearch} disabled={welderPaid}
                  onChange={(e) => {
                    setCommSearch(e.target.value);
                    setCommWelderId("");
                    setCommWelderName(e.target.value);
                  }}
                  placeholder="Welder ka naam ya mobile type karein…"
                  className="bg-slate-800/60 border-amber-500/40 text-white h-10 rounded-sm"
                  data-testid="commission-welder-search"
                />
                {commSearch && !commWelderId && (() => {
                  const q = commSearch.toLowerCase();
                  const matches = (welders || []).filter((w) =>
                    (w.name || "").toLowerCase().includes(q) ||
                    (w.mobile || "").includes(q) ||
                    (w.shop_name || "").toLowerCase().includes(q)
                  ).slice(0, 6);
                  if (matches.length === 0) return null;
                  return (
                    <div className="absolute z-20 mt-1 w-full rounded-sm bg-slate-900 border border-amber-500/40 shadow-xl max-h-56 overflow-auto">
                      {matches.map((w) => (
                        <button key={w.id} type="button"
                          onClick={() => {
                            setCommWelderId(w.id);
                            setCommWelderName(w.name || "");
                            setCommWelderMobile(w.mobile || "");
                            setCommSearch((w.name || "") + (w.shop_name ? ` · ${w.shop_name}` : ""));
                          }}
                          className="block w-full text-left px-3 py-2 hover:bg-amber-500/10 border-b border-slate-800 last:border-b-0"
                          data-testid={`commission-welder-opt-${w.id}`}
                        >
                          <div className="text-sm text-white font-bold">{w.name}</div>
                          <div className="text-[11px] text-slate-400">
                            {w.mobile} {w.shop_name ? `· ${w.shop_name}` : ""}
                          </div>
                        </button>
                      ))}
                    </div>
                  );
                })()}
                {commWelderId && (
                  <div className="mt-1 inline-flex items-center gap-1 text-[11px] font-mono-num bg-amber-500/15 text-amber-200 px-2 py-0.5 rounded-sm">
                    ✓ {commWelderName}{commWelderMobile ? ` · ${commWelderMobile}` : ""}
                    <button type="button"
                      onClick={() => { setCommWelderId(""); setCommWelderName(""); setCommWelderMobile(""); setCommSearch(""); }}
                      className="ml-1 text-amber-300 hover:text-white"
                    >×</button>
                  </div>
                )}
                {!commWelderId && commSearch && (
                  <div className="text-[10px] text-slate-500 mt-1">
                    New welder? Yahaan jo likha, wahi naam use hoga (profile mei create nahi hoga — pehle Welders section se banao).
                  </div>
                )}
              </div>

              {commV2 && (
                <CommissionLines kind="welder" lines={cLines} rates={welderRates} editable={!welderPaid}
                  setRate={(i, v) => setWelderRates((p) => ({ ...p, [i]: v }))} />
              )}
              <div className="grid grid-cols-2 gap-3">
                <div className={commV2 ? "hidden" : ""}>
                  <Label className="text-[10px] text-slate-400 tracking-widest">COMMISSION AMOUNT (₹)</Label>
                  <Input
                    type="number" step="0.01" min="0" inputMode="decimal"
                    value={commAmount} disabled={welderPaid}
                    onChange={(e) => { setCommAmount(e.target.value); setCommManual(true); }}
                    placeholder="e.g., 500"
                    className="bg-slate-800/60 border-amber-500/40 text-amber-200 h-10 rounded-sm font-mono-num text-lg font-bold"
                    data-testid="commission-amount-input"
                  />
                  <div className="text-[10px] font-mono-num text-slate-500 mt-1" data-testid="commission-auto-hint">
                    AUTO ₹{autoComm.toLocaleString("en-IN")}
                    {commManual && !welderPaid && (
                      <button type="button" onClick={() => setCommManual(false)} className="ml-2 text-amber-300 hover:text-white" data-testid="commission-reset-auto">RESET TO AUTO</button>
                    )}
                  </div>
                </div>
                <div>
                  <Label className="text-[10px] text-slate-400 tracking-widest">NOTE (optional)</Label>
                  <Input
                    value={commNote}
                    onChange={(e) => setCommNote(e.target.value)}
                    placeholder="e.g., per sqft 50 x 10 sqft"
                    className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm"
                    data-testid="commission-note-input"
                  />
                </div>
              </div>

              {Number(commAmount) > 0 && (
                <div className="rounded-sm bg-amber-500/5 border border-amber-500/30 p-2 text-[11px] font-mono-num text-amber-200">
                  Welder ko <b>₹{Number(welderAmt).toLocaleString("en-IN")}</b> commission milega
                  (Welder Commissions page mei pending list mei dikhega → Pay button daba ke cash settle karein).
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {gateV2 && (
        <div className="surface p-6" data-testid="fabricator-assign-block">
          <Label className="text-cyan-300 font-mono-num text-[10px] tracking-widest">
            Fabricator
          </Label>
          <div className="mt-2 flex flex-col md:flex-row gap-3 items-stretch">
            <Select value={fabricatorId || "none"} onValueChange={(v) => setFabricatorId(v === "none" ? "" : v)}>
              <SelectTrigger className="bg-slate-800/60 border-slate-600 text-white h-11 rounded-sm flex-1" data-testid="fabricator-select">
                <SelectValue placeholder="Fabricator chuno…" />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-600 text-white">
                <SelectItem value="none">— No fabricator (assign later) —</SelectItem>
                {fabricators.map((f) => (
                  <SelectItem key={f.id} value={f.id}>
                    {f.name} · {f.email || f.mobile || "—"}
                    {f.open_orders_count ? ` · ${f.open_orders_count} open` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {fabricators.length === 0 && (
            <div className="text-[11px] text-amber-300 mt-2">
              Abhi tak koi fabricator user nahi hai — <b>Settings → Users</b> me jaake role <b>"fabrication"</b> se user add karo.
            </div>
          )}

          {/* Internal deadline — ADMIN + FABRICATOR only. Hidden from
              customer track page. Helps owner track promised delivery date. */}
          <div className="mt-3 pt-3 border-t border-slate-700/60 flex flex-col md:flex-row md:items-end gap-3">
            <div className="flex-1">
              <Label className="text-amber-300 font-mono-num text-[10px] tracking-widest">
                Internal deadline
              </Label>
              <input
                type="date"
                value={deadlineDate}
                onChange={(e) => setDeadlineDate(e.target.value)}
                min={istToday()}
                className="bg-slate-800/60 border border-amber-500/40 text-amber-100 h-11 rounded-sm mt-1.5 font-mono-num px-3 w-full"
                data-testid="new-order-deadline"
              />
            </div>
          </div>
        </div>
      )}

      {gateV2 && (
        <label className="surface p-3 rounded-sm flex items-center gap-2 text-sm text-slate-200" data-testid="attach-spec-toggle">
          <input type="checkbox" checked={attachSpec} onChange={(e) => setAttachSpec(e.target.checked)} data-testid="attach-spec-checkbox" />
          Attach spec sheets (bill ke baad har sub-type ki spec PDF)
        </label>
      )}
      </div>

      <aside className="space-y-4 lg:sticky lg:top-24 min-w-0" data-testid="order-summary">
        <div className="surface p-6 space-y-3">
          <div className="font-semibold text-slate-100 mb-1">Summary</div>
          <Row label={projectType === "gate_railing" ? "Items" : "Sheets"}
            value={<span className="text-slate-100">{totals.lines.length} · {totals.sqft} sqft</span>} />
          {billingMode !== "wholesale" && (
            <>
              <Row label="Subtotal" value={<span className="text-slate-100" data-testid="summary-subtotal">₹{(totals.gross || 0).toLocaleString("en-IN")}</span>} />
              <Row label="Discount" value={<span className="text-slate-100" data-testid="summary-discount">₹{round2((totals.gross || 0) - totals.amount).toLocaleString("en-IN")}</span>} />
            </>
          )}
          <div className="border-t border-slate-700/70 pt-3 flex items-center justify-between">
            <span className="font-semibold text-slate-100">Total</span>
            <span className="text-2xl font-semibold text-slate-50" data-testid="summary-total">₹{totals.amount.toLocaleString("en-IN")}</span>
          </div>
          {!isEdit && (
            <>
              <div className="grid grid-cols-2 gap-3 pt-2">
                <Field label="Advance">
                  <Input
                    type="number" min="0" step="0.01"
                    value={advanceAmount}
                    onChange={(e) => setAdvanceAmount(e.target.value)}
                    placeholder="₹0"
                    className="bg-slate-800/60 border-slate-600 text-white h-11 rounded-sm"
                    data-testid="advance-amount-input"
                  />
                </Field>
                <Field label="Mode">
                  <Select value={advanceMode} onValueChange={setAdvanceMode}>
                    <SelectTrigger className="bg-slate-800/60 border-slate-600 text-white h-11 rounded-sm" data-testid="advance-mode-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-600 text-white">
                      {modes.map((m) => (
                        <SelectItem key={m.key} value={m.key}>{m.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              </div>
              {advanceAdjust > 0 && (
                <div className="flex items-center justify-between text-sm" data-testid="summary-advance-adjust">
                  <span className="text-emerald-600">Customer advance se cut</span>
                  <span className="text-emerald-600 font-medium">−₹{advanceAdjust.toLocaleString("en-IN")}</span>
                </div>
              )}
              <div className="flex items-center justify-between rounded-sm bg-slate-700/40 px-4 py-3">
                <span className="text-slate-300">Balance due</span>
                <span data-testid="balance-value" className="font-semibold text-slate-50">₹{balance.toLocaleString("en-IN")}</span>
              </div>
            </>
          )}
          {isAdmin && projectType === "sheet_cutting" && (
            <details className="pt-3 border-t border-slate-700/70" data-testid="cost-profit-toggle">
              <summary className="cursor-pointer text-sm text-slate-400">Cost &amp; profit · admin only</summary>
              <CostPreviewBox mode={billingMode} totals={totals} materialItems={materialItems} machineCost={machineCost}
                welderComm={commEnabled ? welderAmt : 0} staffOn={staffComm} staffAmount={commV2 ? staffAmt : null} wKg={wKg} />
            </details>
          )}
        </div>

        <div className="surface p-6 space-y-4">
          <Field label="Notes">
            <Input
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="bg-slate-800/60 border-slate-600 text-white h-11 rounded-sm"
              data-testid="notes-input"
            />
          </Field>
          {!isEstimate && !isEdit && (
            <label className="flex items-center gap-3 cursor-pointer" data-testid="emergency-toggle-label">
              <input
                type="checkbox"
                checked={isEmergency}
                onChange={(e) => setIsEmergency(e.target.checked)}
                className="w-4 h-4 accent-red-500 cursor-pointer"
                data-testid="emergency-checkbox"
              />
              <span className={"text-[15px] font-medium " + (isEmergency ? "text-red-500" : "text-slate-100")}>Urgent order</span>
            </label>
          )}
        </div>

        <div className="flex gap-3">
          {isEdit && onCancelEdit && (
            <Button
              type="button"
              onClick={onCancelEdit}
              variant="outline"
              className="h-12 rounded-sm border-slate-700 bg-slate-800 text-slate-100 hover:bg-slate-700/60 px-6"
              data-testid="cancel-edit-order-button"
            >
              Cancel
            </Button>
          )}
          <Button
            type="submit"
            disabled={busy}
            data-testid={isEdit ? "save-edit-order-button" : "create-order-button"}
            className="h-14 btn-industrial rounded-sm flex-1 text-base"
          >
            {busy
              ? (isEdit ? "Saving…" : (isEstimate ? "Saving…" : "Creating…"))
              : (isEdit ? "Save changes" : (isEstimate ? "Create estimate" : `Create order · ₹${totals.amount.toLocaleString("en-IN")}`))}
          </Button>
        </div>
      </aside>
      </div>
    </form>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <div className={"space-y-1.5 " + className}>
      <Label className="text-sm font-medium text-slate-300">{label}</Label>
      {children}
    </div>
  );
}
function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-slate-400 text-[15px]">{label}</span>
      <span>{value}</span>
    </div>
  );
}
function round2(n) { return Math.round((Number(n) || 0) * 100) / 100; }
function round3(n) { return Math.round((Number(n) || 0) * 1000) / 1000; }

const WORK_TYPES = [
  { key: "gate", label: "Gate" },
  { key: "railing", label: "Railing" },
  { key: "window", label: "Window" },
  { key: "front_elevation", label: "Front Elevation" },
];

function WorkTypePicker({ value, onChange }) {
  return (
    <div className="surface p-4 rounded-sm border-l-4 border-violet-500/60" data-testid="work-type-block">
      <div className="text-[10px] font-mono-num tracking-widest text-violet-300 mb-2">TYPE OF WORK</div>
      <div className="flex gap-2 flex-wrap">
        {WORK_TYPES.map((w) => (
          <button key={w.key} type="button" onClick={() => onChange(w.key)} data-testid={`work-type-${w.key}`}
            className={"h-9 px-4 rounded-sm border text-xs font-mono-num tracking-widest transition-colors " +
              (value === w.key ? "bg-violet-500 border-violet-500 text-white" : "border-slate-600 text-slate-400 hover:text-slate-200")}>
            {w.label.toUpperCase()}
          </button>
        ))}
      </div>
    </div>
  );
}
